import React, { useEffect, useState } from 'react';
import '../styles/Home.css';
import '../styles/Review.css';
import { FaEnvelope, FaMapPin, FaPhone } from 'react-icons/fa';
import { FaChevronLeft, FaChevronRight, FaFacebook, FaGlobe, FaInstagram, FaTiktok } from 'react-icons/fa6';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { getAmenityIcon } from '../constants/amenityIcons';
import ContactWebsite from '../components/ContactWebsite';
import VenueFeatureCards from '../components/VenueFeatureCards';
import axiosPost from '../helpers/axios-helper';
import MapDisplay from '../components/MapDisplay';
import ImageLightbox from '../components/ImageLightbox';
import config from '../constants/config';
import VenueButtons from '../components/VenueButtons';
import BackButton from '../components/BackButton';

export default function ServicesDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const [itemId, setItemId] = useState(Number(queryParams.get('id')) || 0);

  const slugify = (str) => {
    try {
      return (str || '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    } catch { return ''; }
  };

  const [review, setReview] = useState({
    id: itemId, type: 'service', title: '', heroImage: '',
    contact: { address: '', phone: '', email: '', website: '' },
    socials: { instagram: { url: '' }, facebook: { url: '' }, tiktok: { url: '' } },
    amenities: [], content: '', sections: [],
    queerOwned: false, serviceType: '', price: '', duration: '', mapLocation: null
  });

  const [heroIndex, setHeroIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    if (!itemId && slug) {
      const controller = new AbortController();
      axiosPost('services/list', { 'Content-Type': 'application/json' }, { page: 1, per_page: 1000 },
        (resp) => {
          const items = Array.isArray(resp?.data?.items) ? resp.data.items : [];
          const found = items.find(it => slugify(it.title || '') === slug);
          if (found?.id) setItemId(Number(found.id));
        }, controller
      );
      return () => controller.abort();
    }
    if (!itemId) return;

    const controller = new AbortController();
    axiosPost('services/get', { 'Content-Type': 'application/json' }, { id: itemId },
      (resp) => {
        if (resp?.success && resp?.data?.service) {
          const h = resp.data.service;
          const extras = Array.isArray(h?.extras) ? h.extras : [];
          const map = {};
          extras.forEach(e => { if (e?.section_key) map[e.section_key] = e; });

          const overviewHtml = (map.overview?.description || h.description || '').trim();
          const heroImage = h.image || '';
          const parseImages = (val) => {
            if (!val) return [];
            if (Array.isArray(val)) return val.map(v => (v ?? '').toString().trim()).filter(Boolean);
            return String(val).split(/\r?\n|,/).map(s => s.trim()).filter(Boolean);
          };
          let images = parseImages(h.images || '');
          if (!images.length && heroImage) images = [heroImage];

          const sections = extras
            .filter(e => (e?.section_key || '').toLowerCase() !== 'overview')
            .filter(e => (e?.description || '').trim() !== '')
            .sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0) || (a?.section_key || '').localeCompare(b?.section_key || ''))
            .map(e => ({
              key: e.section_key,
              title: (e.title && e.title.trim()) || (e.section_key || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
              html: e.description
            }));

          const rawAmenities = h.amenities;
          let amenities = [];
          if (typeof rawAmenities === 'string' && rawAmenities.trim()) {
            amenities = rawAmenities.split(/\r?\n|,/).map(s => s.trim()).filter(Boolean).map(am => ({ amenity: am }));
          } else if (Array.isArray(rawAmenities)) {
            amenities = rawAmenities.map(s => (s ?? '').toString().trim()).filter(Boolean).map(am => ({ amenity: am }));
          }

          let mapLocation = null;
          try { if (h.location && typeof h.location === 'string' && h.location.trim().startsWith('{')) mapLocation = JSON.parse(h.location); } catch (e) {}

          setReview({
            id: h.id, type: 'service', title: h.title || '', heroImage, images,
            contact: { address: h.address || h.location || '', phone: h.phone || '', email: h.email || '', website: h.website || '' },
            socials: { instagram: { url: h.instagram_url || '' }, facebook: { url: h.facebook_url || '' }, tiktok: { url: h.tiktok_url || '' } },
            amenities, content: overviewHtml ? `<bold>Overview</bold><br/><br/>${overviewHtml}` : '',
            sections,
            queerOwned: (Number(h.queer_owned || 0) === 1) || (h.queer_owned === true),
            serviceType: h.service_type || '', price: h.price || '', duration: h.duration || '',
            mapLocation
          });

          if (!slug && queryParams.get('id')) {
            const s = slugify(h.title || '');
            if (s) navigate(`/service/${s}`, { replace: true });
          }
        }
      }, controller
    );
    return () => controller.abort();
  }, [itemId, slug]);

  const nextHero = () => { const len = review.images?.length || 0; if (len <= 1) return; setHeroIndex((prev) => (prev + 1) % len); };
  const prevHero = () => { const len = review.images?.length || 0; if (len <= 1) return; setHeroIndex((prev) => (prev - 1 + len) % len); };

  return (
    <div style={{ paddingTop: '90px' }}>
      <div className='container'>
        <BackButton />
        <div className='review-header'>
          <div className='review-header-info'>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 className='review-title'>{review.title}</h1>
              {review.queerOwned && (
                <div title='Queer Owned' style={{ background: 'linear-gradient(#ffffff, #ffffff) padding-box, linear-gradient(90deg, #e91e63, #9c27b0) border-box', border: '1px solid transparent', color: '#2c2c2c', padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, letterSpacing: '0.5px' }}>Queer Owned</div>
              )}
              {review.serviceType && (
                <div style={{ background: '#f0f0f0', color: '#2c2c2c', padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{review.serviceType}</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 15, flexWrap: 'wrap', marginTop: 5 }}>
              {review.contact.address && (
                <div className='hotel-location'><FaMapPin size={12} color={'#2c2c2c'} /><div className='hotel-address'>{String(review.contact.address).toUpperCase()}</div></div>
              )}
              {review.price && <div style={{ fontSize: 13, color: '#555', fontWeight: 600 }}>{review.price}</div>}
              {review.duration && <div style={{ fontSize: 13, color: '#555' }}>{review.duration}</div>}
            </div>
          </div>
        </div>
      </div>

      <div className={`review-gallery-grid ${review.images?.length <= 1 ? 'single-image' : ''} ${review.images?.length === 2 ? 'two-images' : ''}`}>
        <div className='review-gallery-main-image' style={{ backgroundImage: `url('${review.images?.[0] || review.heroImage || ''}')` }} onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}>
          {review.images?.length > 0 && (<div className='review-gallery-count'><span style={{ marginRight: 6 }}>📷</span> {review.images.length}</div>)}
        </div>
        {review.images?.length > 1 && (
          <div className='review-gallery-side'>
            {review.images.slice(1, 4).map((img, idx) => (
              <div key={idx} className='review-gallery-side-image' style={{ backgroundImage: `url('${img}')` }} onClick={() => { setLightboxIndex(idx + 1); setLightboxOpen(true); }}>
                {idx === 2 && review.images.length > 4 && (<div className='review-gallery-more-overlay'>+{review.images.length - 4} more</div>)}
              </div>
            ))}
          </div>
        )}
      </div>

      {lightboxOpen && review.images?.length > 0 && (
        <ImageLightbox images={review.images} initialIndex={lightboxIndex} onClose={() => setLightboxOpen(false)} />
      )}

      <div className='container'>
        <div className='review-content-splitter'>
          <div className='review-content'>
            <VenueButtons itemType='service' itemId={review.id} />

            <VenueFeatureCards
              itemType='service'
              itemId={review.id}
              venuePath={`/service/${slugify(review.title)}`}
              venueTitle={review.title}
            />

            {review.amenities?.length > 0 && (
              <div className='review-amenities'>
                <div className='review-amenities-title' style={{ fontFamily: 'Lato' }}>AMENITIES</div>
                <div className='review-amenities-container'>
                  {review.amenities.map((am, index) => { const Icon = getAmenityIcon(am.amenity); return (<div className='ammenity' key={index}><Icon size={14} color={'#2c2c2c'} /><span>{am.amenity}</span></div>); })}
                </div>
              </div>
            )}
            {review.content && <div className='review-amenities-title'>Overview</div>}
            {review.content && (
              <div className='rich-text' style={{ letterSpacing: '1px', fontSize: 14, color: '#2b2b2b', marginBottom: 50, lineHeight: '1.5' }}
                dangerouslySetInnerHTML={{ __html: review.content.replace(/<bold>\s*Overview\s*<\/bold>\s*<br\s*\/?>(\s*<br\s*\/?>(\s*<br\s*\/?>)?)?/i, '').replace(/<bold>/g, '<strong>').replace(/<\/bold>/g, '</strong>').replace(/color:\s*[^;"}]+;?/gi, '') }} />
            )}
            {review.sections?.map((sec, idx) => (
              <div key={sec.key || idx}>
                <div className='review-amenities-title'>{sec.title}</div>
                <div className='rich-text' style={{ letterSpacing: '1px', fontSize: 14, color: '#2b2b2b', marginBottom: 60, lineHeight: '1.5' }}
                  dangerouslySetInnerHTML={{ __html: (sec.html || '').replace(/<bold>/g, '<strong>').replace(/<\/bold>/g, '</strong>').replace(/color:\s*[^;"}]+;?/gi, '') }} />
              </div>
            ))}
          </div>

          <div className='review-side-content'>
            <div className='review-location-title'>LOCATION</div>
            {review.mapLocation && review.mapLocation.lat && review.mapLocation.lng ? (
              <MapDisplay location={review.mapLocation} apiKey={config.googleMaps.apiKey} title={review.title} height="300px" />
            ) : (<div className='review-location-map'></div>)}
            {(review.contact.phone || review.contact.email || review.contact.website) && (
              <div className='review-contact-title'>CONTACT INFORMATION</div>
            )}
            {review.contact.phone && (<div className='review-contact-item'><FaPhone size={16} color='#2c2c2c' /><div className='review-contact-text'>{review.contact.phone}</div></div>)}
            {review.contact.email && (<div className='review-contact-item'><FaEnvelope size={16} color='#2c2c2c' /><div className='review-contact-text'>{review.contact.email}</div></div>)}
            {review.contact.website && (<div className='review-contact-item'><FaGlobe size={16} color='#2c2c2c' /><ContactWebsite url={review.contact.website} /></div>)}
            {(review.socials?.instagram?.url || review.socials?.facebook?.url || review.socials?.tiktok?.url) && (
              <>
                <div className='review-contact-title' style={{ marginTop: 15 }}>SOCIAL MEDIA</div>
                <div className='review-social-icons'>
                  {review.socials?.instagram?.url && (<a href={review.socials.instagram.url} target='_blank' rel='noreferrer' className='review-social-icon'><FaInstagram size={20} color='#2c2c2c' /></a>)}
                  {review.socials?.facebook?.url && (<a href={review.socials.facebook.url} target='_blank' rel='noreferrer' className='review-social-icon'><FaFacebook size={20} color='#2c2c2c' /></a>)}
                  {review.socials?.tiktok?.url && (<a href={review.socials.tiktok.url} target='_blank' rel='noreferrer' className='review-social-icon'><FaTiktok size={20} color='#2c2c2c' /></a>)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
